import { useServiceToken } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';

import { makeAuthenticatedGetRequest, API_CONFIG } from '../../api';
import { useJudgeDetails as useJudgeDetailsLegacy } from '../../JudgeSearch/api';

import type { OpinionsResponse, PACERResponse } from '../../types';

const PAGE_SIZE = 100;

export const useJudgeDetails = useJudgeDetailsLegacy;

export async function fetchJudgeAssignedCases(
  judgeId: number,
  token: string
): Promise<PACERResponse> {
  const query = `(assigned_to_id:${judgeId} OR referred_to_id:${judgeId})`;
  const searchParams = new URLSearchParams({
    type: 'r',
    order_by: 'score desc',
    page_size: String(PAGE_SIZE),
  });
  const url = `${
    API_CONFIG.BASE_URL
  }/legal/search/?${searchParams.toString()}&q=${encodeURIComponent(query)}`;
  return makeAuthenticatedGetRequest<PACERResponse>(url, token);
}

export function useJudgeAssignedCases(judgeId: number) {
  const { data: tokenData, isLoading: isTokenLoading } =
    useServiceToken('oram');
  const query = useQuery(
    ['judge-assigned-cases-v2', judgeId],
    () =>
      tokenData?.token
        ? fetchJudgeAssignedCases(judgeId, tokenData.token)
        : Promise.reject(new Error('Token not available')),
    {
      enabled: !!judgeId && !isTokenLoading && !!tokenData?.token,
      staleTime: API_CONFIG.STALE_TIME,
      cacheTime: API_CONFIG.CACHE_TIME,
      refetchOnWindowFocus: false,
    }
  );
  return {
    ...query,
    isTokenLoading,
  };
}

const OPINIONS_PAGE_SIZE = 100;

export async function fetchJudgeAuthoredOpinions(
  judgeId: number,
  token: string
): Promise<OpinionsResponse> {
  const query = `(author_id:${judgeId} OR panel_ids:${judgeId})`;
  const searchParams = new URLSearchParams({
    type: 'o',
    order_by: 'score desc',
    page_size: String(OPINIONS_PAGE_SIZE),
    stat_Published: 'on',
    stat_Unpublished: 'on',
  });
  const url = `${
    API_CONFIG.BASE_URL
  }/legal/search/?${searchParams.toString()}&q=${encodeURIComponent(query)}`;
  return makeAuthenticatedGetRequest<OpinionsResponse>(url, token);
}

export function useJudgeAuthoredOpinions(judgeId: number) {
  const { data: tokenData, isLoading: isTokenLoading } =
    useServiceToken('oram');
  const query = useQuery(
    ['judge-authored-opinions-v2', judgeId],
    () =>
      tokenData?.token
        ? fetchJudgeAuthoredOpinions(judgeId, tokenData.token)
        : Promise.reject(new Error('Token not available')),
    {
      enabled: !!judgeId && !isTokenLoading && !!tokenData?.token,
      staleTime: API_CONFIG.STALE_TIME,
      cacheTime: API_CONFIG.CACHE_TIME,
      refetchOnWindowFocus: false,
    }
  );
  return {
    ...query,
    isTokenLoading,
  };
}
